/**
 * Per-user tabular-part prefs (hidden columns, widths, sort, group-by,
 * aggregates, expanded groups) for the App-mode tabular grid.
 * Per-user, connection/object/tabular-part-scoped prefsStore —
 * same persistence tier and merge pattern as `useSavedViews`.
 */
import { useCallback, useEffect, useState } from 'react';
import { prefsStore } from '@/app/preferences/prefsStore';
import type { TabularSort } from './tabularViewModel';
import { sanitizeAggregatesLayout, type AggregatesLayout } from '@/shared/listViews/aggregatesLayout';

export type { TabularSort };

export interface TabularPrefs {
  hidden: string[];
  widths: Record<string, number>;
  sort: TabularSort;
  groupBy: string[];
  /** "field:op" entries, e.g. "amount:sum" — list-compatible shape. */
  aggregates: string[];
  aggregatesLayout: AggregatesLayout;
  /** Σ field order; empty = column LTR. */
  aggregatesFieldOrder: string[];
  /** Whitelist of expanded group keys (see tabularViewModel expandAllDefault semantics). */
  expandedGroups: string[];
}

const DEFAULTS: TabularPrefs = {
  hidden: [],
  widths: {},
  sort: null,
  groupBy: [],
  aggregates: [],
  aggregatesLayout: 'horizontal',
  aggregatesFieldOrder: [],
  expandedGroups: [],
};

export function tabularPrefsKey(cid: string, objectName: string, tabularName: string): string {
  return `tabular_prefs:${cid}:${objectName}:${tabularName}`;
}

function isTabularSort(v: unknown): v is TabularSort {
  if (v === null) return true;
  if (!v || typeof v !== 'object') return false;
  const s = v as Partial<{ field: unknown; dir: unknown }>;
  return typeof s.field === 'string' && (s.dir === 'asc' || s.dir === 'desc');
}

function sanitizeStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function sanitizeWidths(v: unknown): Record<string, number> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(v as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
}

export function loadTabularPrefs(key: string): TabularPrefs {
  try {
    const raw = prefsStore.getItem(key);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return DEFAULTS;
    const p = parsed as Partial<TabularPrefs>;
    return {
      hidden: sanitizeStringArray(p.hidden),
      widths: sanitizeWidths(p.widths),
      sort: isTabularSort(p.sort) ? p.sort : null,
      groupBy: sanitizeStringArray(p.groupBy),
      aggregates: sanitizeStringArray(p.aggregates),
      aggregatesLayout: sanitizeAggregatesLayout(p.aggregatesLayout),
      aggregatesFieldOrder: sanitizeStringArray(p.aggregatesFieldOrder),
      expandedGroups: sanitizeStringArray(p.expandedGroups),
    };
  } catch {
    return DEFAULTS;
  }
}

export function saveTabularPrefs(key: string, prefs: TabularPrefs): void {
  try {
    prefsStore.setItem(key, JSON.stringify(prefs));
  } catch {
    /* private browsing */
  }
}

export function useTabularPrefs(connectionId: string, objectName: string, tabularName: string) {
  const key = tabularPrefsKey(connectionId, objectName, tabularName);
  const [prefs, setPrefs] = useState<TabularPrefs>(() => loadTabularPrefs(key));

  useEffect(() => {
    setPrefs(loadTabularPrefs(key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, objectName, tabularName]);

  const persist = useCallback(
    (next: TabularPrefs) => {
      setPrefs(next);
      saveTabularPrefs(key, next);
    },
    [key]
  );

  const updatePrefs = useCallback(
    (patch: Partial<TabularPrefs>) => {
      setPrefs((prev) => {
        const next = { ...prev, ...patch };
        saveTabularPrefs(key, next);
        return next;
      });
    },
    [key]
  );

  const resetPrefs = useCallback(() => persist(DEFAULTS), [persist]);

  return { prefs, updatePrefs, resetPrefs };
}
